import { useLoaderData } from 'react-router';

import { getProductRangeUrl } from '~/modules/product/util/get-product-range-url';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { useProductPrice } from '../purchase/use-product-price';
import { groupApprovedFacetOptions } from './group-facet-options';
import { useSizesInUse } from '../purchase/purchase-flow-parts';
import type {
    ProductBreadcrumb,
    ProductCustomSection,
    ProductInfoItem,
} from './product-info';

/** The CMS LibraryTab whose content is the specifications table. */
const SPECIFICATIONS_TAB_TITLE = 'Specifications';

/**
 * CMS tabs that duplicate what the page already shows: "Specifications" is the
 * list's Specs value, and "Product Range" duplicates the built-in "Other
 * Products in this range" carousel. Case-insensitive.
 */
const EXCLUDED_TAB_TITLES = [
    SPECIFICATIONS_TAB_TITLE.toLowerCase(),
    'product range',
];
const isExcludedTab = (title: string) =>
    EXCLUDED_TAB_TITLES.includes(title.trim().toLowerCase());

/** How many values a one-line summary prints before it counts the rest. */
const SUMMARY_MAX = 3;

/** "A, B, C +4 more" — keeps a value one line even with 99 colours. */
const joinWithMore = (values: string[], max = SUMMARY_MAX): string =>
    values.length <= max
        ? values.join(', ')
        : `${values.slice(0, max).join(', ')} +${values.length - max} more`;

export interface ProductDetailItems {
    breadcrumb: ProductBreadcrumb[];
    /**
     * The list beside the picture: the facts with more than one answer — a
     * range of prices, several sizes, a CMS tab of specifications.
     */
    items: ProductInfoItem[];
    /**
     * The facts with a single answer, in list order, for the Product Details
     * column beside the tags. A fact is one or the other, never both.
     */
    facts: ProductInfoItem[];
    /** The product's own account of itself, set above the list. */
    descriptionHtml?: string | null;
    scopeOfUseHtml?: string | null;
    customSections: ProductCustomSection[];
    /** Nothing to show — the section self-hides. */
    isEmpty: boolean;
}

/**
 * Everything the Downloads + Details section lists. Split from the accordion
 * hook when the section left the accordion: the two now sit in different parts
 * of the page and neither should be building the other's data.
 */
export const useProductDetailItems = (): ProductDetailItems => {
    const { product } = useLoaderData<typeof loader>();
    // Shown here rather than beside the buttons — see `useProductPrice`.
    const { label: priceLabel, isPriceRange } = useProductPrice();

    // The sizes a variant actually uses, the same list the drawer offers.
    const sizesInUse = useSizesInUse();
    const category = product.Categories?.[0];
    const ranges = product.LibraryRanges.map((range) => ({
        value: range.Title.trim(),
        link: product.Professional
            ? getProductRangeUrl(product.Professional, range.Title.trim())
            : null,
    })).filter((range) => range.value);
    const rangeLinks = ranges.filter(
        (range): range is { value: string; link: string } => !!range.link,
    );
    const sizeLabels = (product.Sizes ?? []).map((size) => size.FullTitle);
    // What Dimensions still has to add once Size has spoken.
    //
    // Both rows were built from the same `product.Sizes`, so Dimensions was
    // listing the product's sizes a second time under a second name. Size
    // shows them as chips where a variant uses them and as its own summary
    // where none does, and that summary counts the tail off past three — so
    // Dimensions now carries the sizes no variant offers, or the full list the
    // summary truncated, and drops out when it would only repeat.
    const sizesOnChips = new Set(sizesInUse.map((size) => size.FullTitle));
    const dimensionLabels = sizesInUse.length
        ? sizeLabels.filter((label) => !sizesOnChips.has(label))
        : sizeLabels.length > SUMMARY_MAX
          ? sizeLabels
          : [];
    const facets = groupApprovedFacetOptions(product.FacetOptions ?? []);
    const specificationsTab = (product.LibraryTabs ?? []).find(
        (tab) => tab.Title === SPECIFICATIONS_TAB_TITLE && tab.Content,
    );

    const candidates: ProductInfoItem[] = [
        // What decides the variant — colour, material, size — is chosen in the
        // meta row under the summary, and opens the drawer. The list keeps
        // the facts. A size no variant offers is a fact, so it stays here.
        {
            label: 'Size',
            value: sizesInUse.length ? '' : joinWithMore(sizeLabels),
            singular: sizeLabels.length === 1,
        },
        // A range is an indication, not a quote, and production says so beside
        // it. The revamp printed the figures alone, which reads as the price.
        {
            label: 'Pricing',
            value: priceLabel
                ? isPriceRange
                    ? `${priceLabel}\n*Price indication`
                    : priceLabel
                : '',
            singular: !isPriceRange,
        },
        // Where the product sits in the directory, and the way back out to
        // everything like it. The revamp carried it in the breadcrumb only,
        // which reads as where the reader came from rather than as a fact
        // about the product — and a reader who landed from search has no
        // breadcrumb behind them at all.
        {
            label: 'Category',
            value: category?.PrettyTitle ?? '',
            link: category?.Link ?? null,
            singular: true,
        },
        {
            label: ranges.length > 1 ? 'Ranges' : 'Range',
            value: ranges.map((range) => range.value).join(', '),
            link: ranges.length === 1 ? (ranges[0]?.link ?? null) : null,
            links: rangeLinks.length > 1 ? rangeLinks : undefined,
            newTab: true,
            singular: ranges.length <= 1,
        },
        { label: 'Designer', value: product.Designer ?? '', singular: true },
        { label: 'Brand', value: product.Brand ?? '', singular: true },
        { label: 'Year', value: product.Year ?? '', singular: true },
        ...facets.map((facet) => ({
            label: facet.Label,
            value: facet.options.join(', '),
            singular: facet.options.length === 1,
        })),
        // One size per line — Dimensions is a stack, not a comma list, so this
        // keeps its newlines (whitespace-pre-line).
        {
            label: 'Dimensions',
            value: dimensionLabels.join('\n'),
            singular: dimensionLabels.length === 1,
        },
        { label: 'Specs', value: '', html: specificationsTab?.Content },
        {
            label: 'Performance',
            value: '',
            html: product.PerformanceDescription,
        },
        {
            label: 'Warranty',
            value: product.WarrantyDuration ?? '',
            singular: true,
        },
    ];
    const present = candidates.filter(
        (item) => item.value || item.html || item.node,
    );
    // A single answer is a fact the reader reads off at a glance, so it goes
    // up beside the tags; the list keeps what takes more than a line.
    const facts = present.filter((item) => item.singular && !item.html);
    const items = present.filter((item) => !facts.includes(item));

    // Custom CMS tabs (e.g. "Motion technologies", "Programme") fold in as
    // labelled rows rather than each becoming its own numbered accordion row.
    const customSections = (product.LibraryTabs ?? [])
        .filter((tab) => !isExcludedTab(tab.Title) && tab.Content)
        .map((tab) => ({ title: tab.Title, html: tab.Content as string }));

    const breadcrumb: ProductBreadcrumb[] = [
        { label: 'Home', to: '/' },
        { label: 'Products', to: '/products' },
        ...(category?.PrettyTitle && category.Link
            ? [{ label: category.PrettyTitle, to: category.Link }]
            : []),
    ];

    return {
        breadcrumb,
        items,
        facts,
        // Above the list rather than the first row of it: the description is
        // the product's own account of itself, not one specification among
        // others, and it was the only row anyone had to open to read.
        descriptionHtml: product.Content,
        scopeOfUseHtml: product.ScopeOfUseDescription,
        customSections,
        isEmpty:
            !product.ScopeOfUseDescription &&
            !product.Content &&
            items.length === 0 &&
            customSections.length === 0,
    };
};
